(ns story-builder.story-editor.controller.util
  (:require
   [cljs.math]
   [clojure.string :as str]
   [story-builder.story-editor.breakpoint.util :as breakpoint.util]
   [story-builder.story-editor.interactive.util :as interactive.util]
   [story-builder.story-editor.layer.util :as layer.util]
   [story-builder.story-editor.scene-control.util :as scene-control.util]
   [story-builder.util.core :as util]
   [story-builder.util.dom :as util.dom]
   [story-builder.util.math :as util.math]))

(def text-layer-types #{"title" "subheading" "text" "date" "author" "rich-text" "captions"})

(defn angle-between [v1 v2]
  (cljs.math/to-degrees (- (apply js/Math.atan2 (reverse v1))
                           (apply js/Math.atan2 (reverse v2)))))

(defn deg-angle-difference
  "The smallest (i.e. within [-180, 180]) oriented angle in degrees from deg-angle1 to deg-angle1,
  where deg-angle1 and deg-angle2 are expressed in degrees."
  [deg-angle1 deg-angle2]
  (-> (- (mod deg-angle2 360) (mod deg-angle1 360))
      (+ 180)
      (mod 360)
      (- 180)))

(defn from-point
  [[x y] [x' y']]
  [(- x' x) (- y' y)])

(defn center-on
  [rect [x y]]
  (-> rect
      (assoc :left (- x (/ (:width rect) 2)))
      (assoc :top (- y (/ (:height rect) 2)))))

(defn transform-deg-rotation [transform]
  (if (and (string? transform) (str/includes? transform "rotate"))
    (-> (re-matches #".*rotate\((-?\d+(\.\d+)?)deg\).*" transform)
        second
        js/parseFloat)
    0))

(defn translate-rect
  [rect [x-op y-op]]
  (cond-> rect
    x-op
    (update :left (fn [left] (x-op left (/ (:width rect) 2))))
    y-op
    (update :top (fn [top] (y-op top (/ (:height rect) 2))))))

(defn scene-percentage-rect
  [rect scene-rect reference-em %-width? %-height?]
  (-> rect
      (update :left #(* (/ % (:width scene-rect)) 100))
      (update :top #(* (/ % (:height scene-rect)) 100))
      (update :width (if %-width?
                       #(* (/ % (:width scene-rect)) 100)
                       #(/ % reference-em)))
      (update :height (if %-height?
                        #(* (/ % (:height scene-rect)) 100)
                        #(/ % reference-em)))))

(defn scene-relative-bounding-rect
  ([el]
   (scene-relative-bounding-rect el ".NWSStory-scene.is-current .NWSStory-layers--unfixed"))
  ([el scene-selector]
   (let [rect       (util.dom/rect el)
         scene-el   (js/document.querySelector scene-selector)
         scene-rect (util.dom/rect scene-el)]
     (-> rect
         (update :left - (:left scene-rect))
         (update :top - (:top scene-rect))
         (update :bottom - (:top scene-rect))
         (update :right - (:left scene-rect))))))

(defn scene-relative-group-bounding-rect
  [els]
  (if (= (count els) 1)
    (let [el   (first els)
          rect (scene-relative-bounding-rect el)]
      rect)
    (let [rects      (map scene-relative-bounding-rect els)
          min-top    (apply min (map :top rects))
          min-left   (apply min (map :left rects))
          max-right  (apply max (map #(+ (:left %) (:width %)) rects))
          max-bottom (apply max (map #(+ (:top %) (:height %)) rects))
          rect       {:top      min-top
                      :left     min-left
                      :right    max-right
                      :bottom   max-bottom
                      :width    (- max-right min-left)
                      :height   (- max-bottom min-top)
                      :corners  [[min-left min-top]
                                 [max-right min-top]
                                 [max-right max-bottom]
                                 [min-left max-bottom]]
                      :rotation 0}]
      rect)))

(defn layer-alignment-vector
  [{:keys [product text-vertical-align type] :as layer} active-breakpoints]
  (let [layer-classes (breakpoint.util/get-props layer active-breakpoints :layer-classes)
        style         (breakpoint.util/get-props layer active-breakpoints :style)
        product-tag?  (= type "product-tag")
        align-v       (case (get layer-classes :align-v
                                 (case text-vertical-align
                                   :top "is-top"
                                   :bottom "is-bottom"
                                   :middle))
                        "is-top"    :top
                        "is-middle" :middle
                        "is-bottom" :bottom
                        :middle)
        align-v       (if product-tag?
                        (let [{:keys [arrow-position]
                               :or   {arrow-position :auto}} product]
                          (if (= arrow-position :auto)
                            (if (> 50 (util/parse-css-value 0 (:top style)))
                              :top
                              :bottom)
                            arrow-position))
                        (or align-v text-vertical-align :middle))]
    [(case (:align-h layer-classes)
       "is-left"   :left
       "is-center" :center
       "is-right"  :right
       :center)
     align-v]))

(defn corners [el]
  (when (instance? js/Element el)
    (let [computed-style (js/getComputedStyle el nil)
          offset-left    (-> (.-left computed-style)
                             (str/replace #"px" "")
                             (js/parseFloat))
          offset-top     (-> (.-top computed-style)
                             (str/replace #"px" "")
                             (js/parseFloat))
          offset-width   (-> (.-width computed-style)
                             (str/replace #"px" "")
                             (js/parseFloat))
          offset-height  (-> (.-height computed-style)
                             (str/replace #"px" "")
                             (js/parseFloat))
          corners        [[offset-left offset-top]
                          [(+ offset-left offset-width) offset-top]
                          [(+ offset-left offset-width) (+ offset-top offset-height)]
                          [offset-left (+ offset-top offset-height)]]]
      corners)))

(defn translate-corners
  [[align-h align-v] corners]
  (let [width  (util.math/distance-between (first corners) (second corners))
        height (util.math/distance-between (first corners) (last corners))]
    (mapv
     (fn [[x y]]
       [(case align-h
          :left x
          :center (- x (/ width 2))
          :right (- x width))
        (case align-v
          :top y
          :middle (- y (/ height 2))
          :bottom (- y height))])
     corners)))

(defn transform-origin
  [[align-h align-v] corners]
  (cond
    (and (= align-h :left) (= align-v :top))
    (first corners)

    (and (= align-h :right) (= align-v :top))
    (second corners)

    (and (= align-h :right) (= align-v :bottom))
    (nth corners 2)

    (and (= align-h :left) (= align-v :bottom))
    (last corners)

    (and (= align-h :center) (= align-v :top))
    (util.math/midpoint (first corners)
                        (second corners))

    (and (= align-h :center) (= align-v :bottom))
    (util.math/midpoint (nth corners 2)
                        (last corners))

    (and (= align-h :left) (= align-v :middle))
    (util.math/midpoint (first corners)
                        (last corners))

    (and (= align-h :right) (= align-v :middle))
    (util.math/midpoint (second corners)
                        (nth corners 2))

    (and (= align-h :center) (= align-v :middle))
    (util.math/midpoint (util.math/midpoint (first corners) (second corners))
                        (util.math/midpoint (nth corners 2) (last corners)))))

(defn actual-corners [layer active-breakpoints]
  (let [el (js/document.getElementById (:id layer))]
    (when (instance? js/Element el)
      (let [deg       (util.dom/deg-rotation el)
            corners   (corners el)
            alignment (layer-alignment-vector layer active-breakpoints)
            corners   (translate-corners alignment corners)
            origin    (transform-origin alignment corners)
            corners   (mapv #(util.math/rotate-around % origin deg) corners)]
        [corners origin]))))

(defn actual-corners->pre-transform-corners
  [[corners origin]]
  (let [deg     (util.math/angle-between (first corners) (second corners))
        corners (mapv #(util.math/rotate-around % origin (- deg)) corners)]
    corners))

(defn match-number-and-unit [s]
  (re-matches #"([0-9.]+)([a-z%]+)" s))

(defn round-to-2-dp [s]
  (let [matches (match-number-and-unit s)]
    (if matches
      (let [[_ num unit] matches
            rounded (js/parseFloat (.toFixed (js/parseFloat num) 2))]
        (str rounded unit))
      s)))

(defn two-dp-precision [m]
  (let [m (reduce
           (fn [res [k v]]
             (if (and (contains? #{:width :height :left :top :right :bottom} k)
                      (string? v))
               (assoc res k (round-to-2-dp v))
               (assoc res k v)))
           {}
           m)]
    m))

(defn %-width?
  [layer active-breakpoints]
  (let [actual-width            (breakpoint.util/get-path layer active-breakpoints [:style :width])
        align-relative-to       (or (breakpoint.util/get-prop layer active-breakpoints :align-relative-to) :responsive)
        stretched-horizontally? (scene-control.util/stretched-horizontally? layer active-breakpoints)]
    (or (or (= align-relative-to :full)
            stretched-horizontally?)
        (and (string? actual-width)
             (not (str/includes? actual-width "em"))
             (not= actual-width "auto")))))

(defn %-height?
  [layer active-breakpoints]
  (let [actual-height         (breakpoint.util/get-path layer active-breakpoints [:style :height])
        align-relative-to     (or (breakpoint.util/get-prop layer active-breakpoints :align-relative-to) :responsive)
        stretched-vertically? (scene-control.util/stretched-vertically? layer active-breakpoints)]
    (or (or (= align-relative-to :full)
            stretched-vertically?)
        (and (string? actual-height)
             (not (str/includes? actual-height "em"))
             (not= actual-height "auto")))))

(defn layer-auto-width?
  [layer active-breakpoints layer-constraints]
  (or (= (breakpoint.util/get-props layer active-breakpoints [:style :width]) "max-content")
      (= (breakpoint.util/get-path layer-constraints active-breakpoints [(:id layer) :keep-ar]) :width)))

(defn layer-auto-height?
  [layer active-breakpoints layer-constraints story-type]
  (or (layer.util/text-layer-type? (:type layer))
      (interactive.util/auto-height? story-type layer)
      (and (= (breakpoint.util/get-path layer active-breakpoints [:style :height]) "auto")
           (not= (breakpoint.util/get-path layer-constraints active-breakpoints [(:id layer) :keep-ar]) :width))
      (= (breakpoint.util/get-path layer-constraints active-breakpoints [(:id layer) :keep-ar]) :height)))